import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Mail, Lock, Key, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ResetPassword: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | null;
    message: string | string[];
  }>({ type: null, message: '' });

  const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/auth`;

  const navigate = useNavigate();
  // Clear notification after 5 seconds
  const showNotification = (type: 'success' | 'error', message: string | string[]) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: null, message: '' }), 5000);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 6) errors.push('Password must be at least 6 characters long');
    return errors;
  };

  const handleRequestOtp = async () => {
    if (!email.trim()) {
      showNotification('error', 'Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      showNotification('error', 'Please enter a valid email address');
      return;
    }

    setLoading(true);
    setNotification({ type: null, message: '' });

    try {
      await axios.post(`${API_BASE_URL}/forgot-password`, { email });
      
      showNotification('success', 'OTP has been sent to your email address. Please check your inbox.');
      setStep(2);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        error?.response?.data?.error ||
        'Failed to send OTP. Please check your email address and try again.';
      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      showNotification('error', 'Please enter the OTP code');
      return;
    }

    if (otp.length !== 6) {
      showNotification('error', 'OTP must be 6 digits long');
      return;
    }

    setLoading(true);
    setNotification({ type: null, message: '' });

    try {
      await axios.post(`${API_BASE_URL}/verify-otp`, { email, otp });
      
      showNotification('success', 'OTP verified successfully');
      setStep(3);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        error?.response?.data?.error ||
        'Invalid or expired OTP. Please try again.';
      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      showNotification('error', 'Please enter a new password');
      return;
    }

    if (!confirmPassword.trim()) {
      showNotification('error', 'Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      showNotification('error', 'Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      showNotification('error', passwordErrors);
      return;
    }

    setLoading(true);
    setNotification({ type: null, message: '' });

    try {
      await axios.post(`${API_BASE_URL}/reset-password`, { 
        email, 
        newPassword 
      });
      
      showNotification('success', 'Password has been reset successfully! You can now log in with your new password.');
      
      // Reset form after successful password reset
      setTimeout(() => {
         navigate('/login'); 
      }, 2000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        error?.response?.data?.error ||
        'Failed to reset password. Please try again.';
      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep = (targetStep: 1 | 2) => {
    setStep(targetStep);
    setNotification({ type: null, message: '' });
  };

  const NotificationComponent = () => {
    if (!notification.type) return null;

    const isSuccess = notification.type === 'success';
    const Icon = isSuccess ? CheckCircle : AlertCircle;
    const bgColor = isSuccess ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
    const textColor = isSuccess ? 'text-green-800' : 'text-red-800';
    const iconColor = isSuccess ? 'text-green-500' : 'text-red-500';

    return (
      <div className={`mt-4 p-4 rounded-md border ${bgColor}`}>
        <div className="flex items-start">
          <Icon className={`w-5 h-5 mt-0.5 mr-3 ${iconColor}`} />
          <div className={`text-sm ${textColor}`}>
            {Array.isArray(notification.message) ? (
              <ul className="space-y-1">
                {notification.message.map((msg, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{msg}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>{notification.message}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProgressIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
          Email
        </span>
        <span className={`text-xs font-medium ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
          Verify
        </span>
        <span className={`text-xs font-medium ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
          Reset
        </span>
      </div>
      <div className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
          step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
        }`}>
          1
        </div>
        <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
          step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
        }`}>
          2
        </div>
        <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
          step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
        }`}>
          3
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600 text-sm">
            {step === 1 && "Enter your email to receive a verification code"}
            {step === 2 && "Enter the verification code sent to your email"}
            {step === 3 && "Create your new password"}
          </p>
        </div>

        <ProgressIndicator />

        {/* Step 1: Email Input */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
            <button
              onClick={handleRequestOtp}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending OTP...
                </div>
              ) : (
                'Send Verification Code'
              )}
            </button>
          </div>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <button
                  onClick={() => handleBackToStep(1)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Change Email
                </button>
              </div>
              <div className="relative">
                <Key className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-lg tracking-widest"
                  maxLength={6}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Code sent to: <span className="font-medium">{email}</span>
              </p>
            </div>
            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Verifying...
                </div>
              ) : (
                'Verify Code'
              )}
            </button>
          </div>
        )}

        {/* Step 3: Password Reset */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <button
                  onClick={() => handleBackToStep(2)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>
              </div>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-2 font-medium">Password requirements:</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• Contains uppercase and lowercase letters</li>
                <li>• Contains at least one number</li>
                <li>• Contains at least one special character</li>
              </ul>
            </div>
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Resetting Password...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        )}

        <NotificationComponent />
      </div>
    </div>
  );
};

export default ResetPassword;